import { IconArrowLeft } from 'central-icons/IconArrowLeft';
import { IconChevronRight } from 'central-icons/IconChevronRight';

import { FLAG_ENABLE_RICS } from '@revelio/core';
import { Button, Text } from '@revelio/ui';

import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../../components/PageConfigProvider/types';
import {
  GEOGRAPHY_GRANULARITY_FILTERS,
  RICS_FILTERS,
  ROLE_GRANULARITY_FILTERS,
} from '../../constants/filter-constants';
import { FilterId } from '../../types/filters';
import styles from './segment-selector.module.css';

export interface Segment {
  label: string;
  value: string[];
}

export const mapSegmentOptionToFilterId = (
  segment: Segment
): FilterId | null => {
  switch (segment.label) {
    case 'Ethnicity':
      return FilterId.ETHNICITY;
    case 'Gender':
      return FilterId.GENDER;
    case 'Geography':
      return FilterId.GEOGRAPHY;
    case 'Highest Degree':
      return FilterId.EDUCATION;
    case 'Industry':
      return FilterId.INDUSTRY;
    case 'Role':
      return FilterId.ROLE;
    case 'Seniority':
      return FilterId.SENIORITY;
    default:
      return null;
  }
};

export const mapSegmentOptionToKey = (segment: Segment): string | null => {
  switch (segment.label) {
    case 'Ethnicity':
      return 'ethnicity';
    case 'Gender':
      return 'gender';
    case 'Geography':
      return 'geography';
    case 'Highest Degree':
      return 'highest_degree';
    case 'Industry':
      return 'industry';
    case 'Role':
      return 'role';
    case 'Seniority':
      return 'seniority';
    default:
      return null;
  }
};

export const mapSegmentKeyToLabel = (
  segmentKey: string | null
): string | null => {
  if (!segmentKey) return null;
  const option = segmentOptions.find((opt) => {
    const key = mapSegmentOptionToKey(opt);
    return key === segmentKey || opt.value.includes(segmentKey);
  });
  return option?.label ?? null;
};

const segmentOptions: Segment[] = [
  { label: 'Ethnicity', value: ['ethnicity'] },
  { label: 'Gender', value: ['gender'] },
  { label: 'Geography', value: GEOGRAPHY_GRANULARITY_FILTERS },
  { label: 'Highest Degree', value: ['highest_degree'] },
  {
    label: 'Industry',
    value: FLAG_ENABLE_RICS ? RICS_FILTERS : ['industry'],
  },
  { label: 'Role', value: ROLE_GRANULARITY_FILTERS },
  { label: 'Seniority', value: ['seniority'] },
];

interface SegmentSelectorProps {
  onSegmentSelect: (segment: Segment) => void;
  onClickBack: () => void;
}

export const SegmentSelector = ({
  onSegmentSelect,
  onClickBack,
}: SegmentSelectorProps) => {
  const { revelioPageId } = useTabContentConfig();
  const isCompanyScreener = revelioPageId === RevelioPageId.COMPANY_SCREENER;

  return (
    <div className={styles.container}>
      {/* SEGMENTS Header with back arrow */}
      <div className={styles.header}>
        <div className={styles.backButton}>
          <IconArrowLeft
            size="1rem"
            className={styles.backIcon}
            onClick={onClickBack}
            data-testid="segment-selector-back"
          />
        </div>
        <Text fontSize="x-sm" weight="semibold" className={styles.headerLabel}>
          SEGMENTS
        </Text>
      </div>

      {/* Segment list */}
      <ul className={styles.segmentList}>
        {segmentOptions
          .filter(
            // No industry segment on company screener
            (option) => !(isCompanyScreener && option.label === 'Industry')
          )
          .map((option, index) => (
            <div
              className={styles.segmentRow}
              onClick={() => onSegmentSelect(option)}
              key={index}
            >
              <li
                className={styles.segmentItem}
                data-testid={`segment-option-${option.label}`}
              >
                {option.label}
              </li>
              <IconChevronRight size="1em" className={styles.optionChevron} />
            </div>
          ))}
      </ul>

      {/* Back link at bottom */}
      <div className={styles.footer}>
        <Button
          variant="link"
          style={{ fontSize: '12px', height: '32px' }}
          onClick={onClickBack}
          autoFocus
        >
          Back
        </Button>
      </div>
    </div>
  );
};
